import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    inject,
    input,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { TransmissionSupplierBasket, UpdateContactPersonsRequest } from '../model/basket.model';

import { EnvService } from '@lib/env';
import { Order } from '../model/order.model';
import { OrderEnum } from '../model/order.enum';
import { BasketTaskComponent, BasketTaskContainerComponent } from '@lib/workflow-integration';
import { firstValueFrom, Observable, takeUntil } from 'rxjs';
import { DialogOptions, IRecord, makeHorizontalStepSlide, SubscriptionHelper } from '@lib/util';
import { OrderIds } from '@lib/order/data-access';
import {
    Basket,
    BasketAddress,
    SupplierBasket,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { CatalogType } from '@lib/catalog/product-tree-filter/ui';
import {
    BasketStore,
    IBudgetExceedance,
    IPurchasingGroupAssignmentIssue,
    IPurchasingGroupResolution,
    OrderOnBehalfTargetUser,
} from '@lib/shared/basket-service';
import { ICompanyCode } from '@lib/company-code';
import { IAccountingBulkChangeSelection } from '@lib/accounting';
import { MatDialog } from '@angular/material/dialog';
import {
    SelectOrderOnBehalfTargetDialogComponent,
    SelectOrderOnBehalfTargetDialogData,
    SelectOrderOnBehalfTargetDialogResult,
} from './select-order-on-behalf-target/select-order-on-behalf-target-dialog.component';

@Component({
    selector: 'basket-overview',
    templateUrl: './basket-overview.component.html',
    styleUrls: ['./basket-overview.component.scss'],
    standalone: false,
    animations: [makeHorizontalStepSlide()],
})
export class BasketOverviewComponent extends SubscriptionHelper implements OnInit, OnChanges {
    formBuilder = inject(UntypedFormBuilder);
    env = inject(EnvService);
    cdr = inject(ChangeDetectorRef);
    private readonly basketStore = inject(BasketStore);
    private readonly dialog = inject(MatDialog);

    @ViewChild('taskContainer', { static: false }) public taskContainer!: BasketTaskContainerComponent;

    taskComponentInstances: ComponentRef<BasketTaskComponent>[] = [];
    stepGroup?: UntypedFormGroup;
    // Active step of the custom stepper (replaces the former MatStepper selectedIndex).
    activeIndex = 0;
    // TODO this is not working because it works with input supplierBaskets which we no longer use
    priceIsZero = false;
    orderStateEnum = OrderEnum;

    finished = false;

    /**
     * "Bestellen im Namen von" (UC_OR_1010, EPROC-11237): target user for the order, if selected.
     * Sourced from {@link BasketStore} (not local state) so the store's reload effects can react to
     * it as soon as it changes. Exposed as the store's Observable directly (consumed via `| async`
     * in the template) rather than a method call, per `code-style.md`.
     */
    readonly targetUser$: Observable<OrderOnBehalfTargetUser | null> = this.basketStore.targetUser;

    //TODO REMOVE THIS INPUT AND SEE WHAT TO DO WITH priceIsZero
    @Input() supplierBaskets: SupplierBasket[] = [];
    @Input() basket: Basket | undefined;
    @Input() deliveryAddress: BasketAddress | undefined;
    @Input() invoiceAddress: BasketAddress | undefined;
    @Input() isLoading = false;
    @Input() initialLoading = true;
    @Input() order!: Order[];
    @Input() orderState!: OrderEnum;
    @Input() orderIds!: OrderIds;
    @Input() isMobile?: boolean;

    @Input() isMoreThanOneInvoiceAddress = false;
    @Input() orderMessage!: string;
    @Input() showTaskAfterBasket!: boolean;
    @Input() purchaseRequisitionStatus!: boolean;
    @Input() isOrderCreationAllowed!: boolean;
    /** "Bestellen im Namen von" (UC_OR_1010, EPROC-11237): whether the button may be shown. */
    @Input() isOrderOnBehalfAllowed = false;
    readonly isOrderCompletionAllowed = input<boolean>();
    @Input() budgetOverruns: IBudgetExceedance[] | null = [];
    @Input() purchasingGroupAssignmentIssues: readonly IPurchasingGroupAssignmentIssue[] = [];
    @Input() resolvedPurchasingGroups: readonly IPurchasingGroupResolution[] = [];
    @Input() purchasingGroupVisible = true;
    @Input() purchasingGroupValidationLoading = false;
    @Input() companyCodes: ICompanyCode[] = [];
    @Input() companyCodeOptionsLoading = false;
    @Input() companyCodeLoadFailed = false;
    @Input() companyCodeUpdating = false;
    // Hides the whole basket (except the company-code selection) behind a loading animation while a
    // company-code change reloads and re-validates all items (EPROC-11028).
    @Input() basketReloading = false;
    @Input() requiresCompanyCodeSelection = false;
    @Input() showCompanyCodeSelection = false;
    @Input() selectedCompanyCodeId: string | null = null;

    @Output() deliveryAddressDialogOpened = new EventEmitter<void>();
    @Output() invoiceAddressDialogOpened = new EventEmitter<void>();
    /** Payload: the id of the selected "Bestellen im Namen von" target user, or null for a regular checkout. */
    @Output() ordered = new EventEmitter<string | null>();
    @Output() demanded = new EventEmitter<void>();
    @Output() transmissionInfoUpdated = new EventEmitter<TransmissionSupplierBasket>();
    @Output() contactPersonsUpdated = new EventEmitter<UpdateContactPersonsRequest>();
    @Output() accountingOptionsSent = new EventEmitter<IAccountingBulkChangeSelection>();
    @Output() tasksCompleted = new EventEmitter<void>();
    @Output() companyCodeSelected = new EventEmitter<string>();
    @Output() basketCleared = new EventEmitter<void>();

    public resultedTasks: IRecord<string>[] = [];
    public companyCodeFilter = '';

    ngOnInit() {
        this.initForms();
        this.updateStepValidity();

        // Child steps emit their validity from ngOnChanges (during change detection); refreshing the
        // snapshot in a microtask keeps the already-rendered stepper-icon bindings from flipping (NG0100).
        this.stepGroup?.statusChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            Promise.resolve().then(() => {
                this.updateStepValidity();
                this.cdr.markForCheck();
            });
        });

        this.basketStore.resetStepper$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.resetStepper();
        });
    }

    // --- Custom stepper -------------------------------------------------------

    get totalSteps(): number {
        return this.showTaskAfterBasket ? 4 : 3;
    }

    get stepLabels(): string[] {
        const labels = ['Warenkorb bearbeiten', 'Zusätzliche Informationen', 'Zusammenfassung'];
        return this.showTaskAfterBasket ? [...labels, 'Freigeber wählen'] : labels;
    }

    get stepIcons(): string[] {
        const icons = ['fas fa-shopping-cart', 'fas fa-address-card', '', ''];
        return icons.slice(0, this.totalSteps);
    }

    // Highest step the user has reached so far. Tracked as a stable field (instead of reading
    // live form validity in the template) to avoid NG0100 when a child emits its validity
    // during change detection.
    private maxVisitedIndex = 0;

    // Step controls in stepper order. A step is only reachable when every control before it is
    // valid - the same gate the "Weiter" buttons apply.
    private readonly stepControlNames = ['supplierOverview', 'basketAddressSelection', 'basketCheck', 'lastStep'];

    // Stable snapshot of each step control's validity. Updated deferred (see ngOnInit) so the
    // template's stepper-icon bindings never read live form state mid-change-detection.
    private stepValidity: boolean[] = [];

    canGoToStep(index: number): boolean {
        if (index < 0 || index >= this.totalSteps || index > this.maxVisitedIndex) {
            return false;
        }
        return this.arePrecedingStepsValidSnapshot(index);
    }

    goToStep(index: number): void {
        if (index < 0 || index >= this.totalSteps || index > this.maxVisitedIndex) {
            return;
        }
        // Authoritative re-validation on click (runs outside change detection): reading live form
        // validity here is safe and blocks jumping past an invalidated step via the stepper icons.
        if (!this.arePrecedingStepsValidLive(index)) {
            return;
        }
        this.setActiveIndex(index);
    }

    private arePrecedingStepsValidSnapshot(index: number): boolean {
        for (let step = 0; step < index; step++) {
            if (this.stepValidity[step] === false) {
                return false;
            }
        }
        return true;
    }

    private arePrecedingStepsValidLive(index: number): boolean {
        for (let step = 0; step < index; step++) {
            if (this.stepGroup?.controls[this.stepControlNames[step]]?.invalid) {
                return false;
            }
        }
        return true;
    }

    private updateStepValidity(): void {
        this.stepValidity = this.stepControlNames.map((name) => !!this.stepGroup?.controls[name]?.valid);
    }

    private goNext(): void {
        this.setActiveIndex(this.activeIndex + 1);
    }

    private goPrevious(): void {
        this.setActiveIndex(this.activeIndex - 1);
    }

    private setActiveIndex(index: number): void {
        const clamped = Math.max(0, Math.min(index, this.totalSteps - 1));
        if (clamped === this.activeIndex) {
            return;
        }
        this.activeIndex = clamped;
        this.maxVisitedIndex = Math.max(this.maxVisitedIndex, clamped);
        // Mirrors the former MatStepper (selectionChange) hook: the tasks step renders its
        // container lazily, so defer until the @ViewChild is resolved.
        if (clamped === 3 && this.showTaskAfterBasket) {
            setTimeout(() => this.displayTasks());
        }
    }

    private resetStepper(): void {
        this.activeIndex = 0;
        this.maxVisitedIndex = 0;
        this.stepGroup?.reset({
            supplierOverview: false,
            basketAddressSelection: false,
            basketCheck: false,
            lastStep: false,
        });
        this.updateStepValidity();
    }

    public get filteredCompanyCodes(): ICompanyCode[] {
        if (!this.companyCodes?.length) {
            return [];
        }
        const filterValue = this.companyCodeFilter.trim().toLowerCase();
        if (!filterValue) {
            return this.companyCodes;
        }
        return this.companyCodes.filter((code) =>
            `${code.code} ${code.name}`.toLowerCase().includes(filterValue),
        );
    }

    public handleCompanyCodeSelection(companyCodeId: string | null): void {
        if (companyCodeId) {
            this.stepGroup?.controls['basketAddressSelection']?.setValue(false);
            this.stepGroup?.controls['basketCheck']?.setValue(false);
            this.companyCodeSelected.emit(companyCodeId);
        }
    }

    public companyCodeLabel(companyCodeId: string | null): string {
        if (!companyCodeId) {
            return '';
        }
        const matching = this.companyCodes.find((code) => code.id === companyCodeId);
        if (!matching) {
            return companyCodeId;
        }
        return `${matching.code} - ${matching.name}`;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['supplierBaskets'] && this.supplierBaskets) {
            this.priceIsZero = this.checkForZeroPrice(this.supplierBaskets);
        }

        if (changes['order'] && this.order.length > 0 && this.showTaskAfterBasket) {
            this.openLastStep();
        }

        if (changes['showTaskAfterBasket'] && this.showTaskAfterBasket && this.order.length > 0) {
            this.openLastStep();
        }
    }

    openAddressSelection(): void {
        if (this.stepGroup?.controls['supplierOverview'].valid) {
            this.goNext();
        }
    }

    openBasketCheck(): void {
        if (this.stepGroup?.controls['basketAddressSelection'].valid) {
            this.goNext();
        }
    }

    // TODO CHECK IF THIS IS USED
    openLastStep(): void {
        if (this.stepGroup?.controls['basketCheck'].valid) {
            this.goNext();
        }
    }

    openBasketSupplier(): void {
        this.goPrevious();
    }

    onCheckout(): void {
        if (!this.showTaskAfterBasket) {
            this.finished = true;
        }
        this.ordered.emit(this.basketStore.getTargetUser()?.id ?? null);
    }

    /** "Bestellen im Namen von" (UC_OR_1010, EPROC-11237): opens the target-user selection dialog. */
    onSelectOrderOnBehalfTarget(): void {
        this.dialog
            .open<SelectOrderOnBehalfTargetDialogComponent, SelectOrderOnBehalfTargetDialogData, SelectOrderOnBehalfTargetDialogResult>(
                SelectOrderOnBehalfTargetDialogComponent,
                DialogOptions.SMALL(new SelectOrderOnBehalfTargetDialogData(this.selectedCompanyCodeId)),
            )
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.basketStore.selectTargetUser$(result.targetUser);
                }
            });
    }

    clearOrderOnBehalfTarget(): void {
        this.basketStore.clearTargetUser$();
    }

    onDemand(): void {
        this.demanded.emit();
    }

    onClearBasket(): void {
        this.basketCleared.emit();
    }

    get hasBasketItems(): boolean {
        return (this.basket?.supplierBaskets ?? []).some((supplierBasket) => supplierBasket.basketItems.length > 0);
    }

    openDeliveryAddressDialog(): void {
        this.deliveryAddressDialogOpened.emit();
    }

    openInvoiceAddressDialog(): void {
        this.invoiceAddressDialogOpened.emit();
    }

    updateTransmissionInfo(transmissionInfoSB: TransmissionSupplierBasket): void {
        this.transmissionInfoUpdated.emit(transmissionInfoSB);
    }

    updateContactPersons(data: UpdateContactPersonsRequest) {
        this.contactPersonsUpdated.emit(data);
    }

    setAllAccountingOptionsAtOnce(selection: IAccountingBulkChangeSelection) {
        this.accountingOptionsSent.emit(selection);
    }

    // TODO CHECK THIS FORM
    private initForms(): void {
        this.stepGroup = this.formBuilder.group({
            supplierOverview: [false, Validators.requiredTrue],
            basketAddressSelection: [false, Validators.requiredTrue],
            basketCheck: [false, Validators.requiredTrue],
            lastStep: [false, Validators.requiredTrue],
        });
    }

    private checkForZeroPrice(supplierBaskets: SupplierBasket[]) {
        let hasZeroPrice = false;

        supplierBaskets.forEach((supplierBasket) => {
            const hasItemWithZeroPrice = supplierBasket.basketItems.some(
                (basketItem) =>
                    basketItem.product.type !== CatalogType.FREIGHT_COST &&
                    (basketItem.price.amount < 0.001 || basketItem.price.amount === undefined),
            );
            if (hasItemWithZeroPrice) {
                hasZeroPrice = true;
            }
        });

        return hasZeroPrice;
    }

    private async displayTasks(): Promise<void> {
        const viewContainerRef = this.taskContainer.viewContainerRef;
        viewContainerRef.clear();
        for (const order of this.orderIds) {
            const componentRef = viewContainerRef.createComponent(BasketTaskComponent);
            this.taskComponentInstances.push(componentRef);
            componentRef.instance.orderId = order.value;
            this.resultedTasks = await firstValueFrom(componentRef.instance.allCompleted);
            componentRef.destroy();
            this.taskComponentInstances = this.taskComponentInstances.filter((c) => c !== componentRef);
        }

        this.checkTaskResolved();
    }

    private checkTaskResolved(): void {
        if (!this.taskComponentInstances.length) {
            this.finished = true;
            this.tasksCompleted.emit();
        }
    }
}
